<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import { formatDate, legalStatus } from './format';
import { sanitizeHtml } from '@/ui/sanitize-html';
import { useAccount } from '@/state/account';

/**
 * Legal: whatever documents the operator has published — terms, privacy,
 * cookies, and any further ones added from the admin panel's Legal section.
 *
 * **The same documents the landing page's footer links**, from the same
 * dynamic list (`GET /api/legal`) and the same `GET /api/cms/legal/:slug` for
 * each one's content — so the version a visitor reads before signing up and
 * the version a player reads in their profile cannot differ, and an operator
 * adding a fourth document shows up here without a deploy. See
 * `state/account.ts`'s `loadLegal()` and `state/legal-slugs.ts`.
 *
 * **Rendered as sanitized markup, not text.** Legal content moved from plain
 * text to Quill-authored HTML on the admin side — a deliberate reversal of
 * this file's previous "never markup" rule. It is still admin-authored content
 * landing on a signed-in player's screen, so it is never trusted raw:
 * `sanitizeHtml()` (DOMPurify underneath) runs on every render before the
 * `v-html` bind. `LegalOverlay.vue` on the landing page makes the identical
 * choice, for the identical reason.
 *
 * **An unpublished document is a real state and says so.** An operator who has not
 * written a cookie policy yet gets a plain line rather than an error or an empty
 * panel, either of which reads as the app being broken.
 */
const account = useAccount();

/** Which document is open, or null for the list. */
const open = ref<string | null>(null);

onMounted(() => {
  account.loadLegal().catch(() => {});
});

const current = computed(() => (open.value ? (account.legal.value[open.value] ?? null) : null));
const currentLabel = computed(
  () => account.legalDocs.value.find((entry) => entry.slug === open.value)?.title ?? '',
);
</script>

<template>
  <div class="pane" data-test="pane-legal">
    <!-- "Documents", not "Legal": the rail chip beside this already says Legal,
         and a block heading that repeats its own nav label spends the one line
         of structure a panel gets on a fact already on screen. Billing's own
         block is "Transactions" for the same reason. -->
    <section v-if="!open" class="block">
      <h3>Documents</h3>
      <p class="hint">
        The documents that govern your account. Written and kept up to date by the
        operator of this server.
      </p>

      <ul class="docs">
        <li v-for="entry in account.legalDocs.value" :key="entry.slug">
          <button
            type="button"
            class="doc"
            :data-test="`legal-${entry.slug}`"
            :disabled="
              !account.loaded.value.legal ||
              legalStatus(account.legal.value[entry.slug] ?? null) === 'unpublished'
            "
            @click="open = entry.slug"
          >
            <span class="doc-name">{{ entry.title }}</span>
            <span
              v-if="!account.loaded.value.legal"
              class="doc-note"
            >Loading…</span>
            <span
              v-else-if="legalStatus(account.legal.value[entry.slug] ?? null) === 'unpublished'"
              class="doc-note"
              :data-test="`legal-${entry.slug}-unpublished`"
            >Not published</span>
            <span v-else class="doc-note">
              Updated {{ formatDate(account.legal.value[entry.slug]?.updatedAt) }}
            </span>
            <svg class="chev" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path
                d="M6 3.5 10.5 8 6 12.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </li>
      </ul>
    </section>

    <section v-else class="block">
      <button type="button" class="back" data-test="legal-back" @click="open = null">
        All documents
      </button>
      <h3 data-test="legal-title">{{ current?.title || currentLabel }}</h3>
      <p v-if="current?.updatedAt" class="hint">
        Last updated {{ formatDate(current.updatedAt) }}.
      </p>
      <div class="prose" data-test="legal-content" v-html="sanitizeHtml(current?.content ?? '')"></div>
    </section>
  </div>
</template>

<style scoped>
@import './pane.css';

.docs {
  margin: 0;
  padding: 0;
  list-style: none;
}

.docs li + li {
  margin-top: 0.5rem;
}

/*
 * A document row is the one thing in this screen that *is* clickable, so it is
 * the one thing that takes the storefront's hover behaviour: a lit border, a
 * plate that warms, and the chevron travelling a couple of pixels toward the
 * direction it points. The lift is on the chevron rather than on the row, so
 * nothing under it moves.
 *
 * It stays a `<button>` with a real `disabled`, and it keeps the `.doc` class:
 * an unpublished document has to be genuinely inert rather than styled to look
 * it, and `e2e/cases/profile.mjs` reads both facts off this element.
 */
.doc {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  min-height: 44px;
  padding: 0.85rem 0.95rem;
  border: 1px solid var(--steel-700);
  border-radius: 3px;
  background: rgba(11, 13, 17, 0.55);
  color: var(--steel-200);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;
}

.doc:hover:not(:disabled) {
  border-color: var(--accent);
  background: rgba(232, 114, 44, 0.07);
}

.doc:hover:not(:disabled) .chev {
  color: var(--accent);
  transform: translateX(2px);
}

.doc:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.doc:disabled {
  cursor: default;
  opacity: 0.55;
}

.doc-name {
  flex: 1;
  font-size: 0.88rem;
}

.doc-note {
  flex: none;
  color: #7d8ca1;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
}

.chev {
  flex: none;
  width: 0.85rem;
  height: 0.85rem;
  color: #7d8ca1;
  transition: color 120ms ease, transform 120ms ease;
}

.doc:disabled .chev {
  visibility: hidden;
}

@media (prefers-reduced-motion: reduce) {
  .doc,
  .chev {
    transition: none;
  }

  .doc:hover:not(:disabled) .chev {
    transform: none;
  }
}

/* The way back out of a document, and it was a 12px-tall bare word — under half
   the touch floor and the smallest control on the screen. It is `pane.css`'s
   `.ghost` (which is `ConfirmPurchase`'s), with a chevron pointing the other
   way; stated here rather than by adding the class in the template, because it
   is the only ghost in the app that leads with a glyph. */
.back {
  display: inline-flex;
  gap: 0.4rem;
  align-items: center;
  align-self: flex-start;
  margin: 0 0 0.9rem;
  padding: 0.5rem 0.9rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: transparent;
  color: var(--steel-200);
  font: inherit;
  font-size: 0.82rem;
  cursor: pointer;
  transition: color 120ms ease, border-color 120ms ease;
}

.back::before {
  content: '←';
  font-size: 0.9em;
  line-height: 1;
}

.back:hover,
.back:focus-visible {
  border-color: var(--steel-400);
  color: #fff;
}

@media (hover: none) and (pointer: coarse) {
  .back {
    min-height: 44px;
  }
}

/*
 * **The one thing on this screen with a measure of its own**, and the deliberate
 * exception to the pane filling its column. Everything else here is a form field
 * or a table cell, both of which want the width; this is running prose, and at
 * 0.84rem across the pane's full ~55rem a line runs past 110 characters, which is
 * roughly double what anybody can track a return sweep across. Capped at 66
 * characters, which is where it stops being a wall.
 *
 * Not centred in the block: it starts where the heading above it starts, so the
 * left edge the eye returns to is the same one for the whole panel.
 */
.prose {
  margin: 0;
  max-width: 66ch;
  color: var(--steel-200);
  font-size: 0.86rem;
  line-height: 1.75;
}

/* Admin-authored HTML now, not newline-separated text — see the header note
   on why this is `v-html` behind `sanitizeHtml()` rather than interpolation.
   Real `<p>`/list elements carry their own block spacing, so no `pre-wrap`. */
.prose :deep(p),
.prose :deep(ul),
.prose :deep(ol) {
  margin: 0 0 0.85em;
}
.prose :deep(p:last-child),
.prose :deep(ul:last-child),
.prose :deep(ol:last-child) {
  margin-bottom: 0;
}
</style>
